import React, { useState, useRef } from 'react'
import { Button, Input } from '@arnsbogroup/react-voicearchive'
import Icon from 'components/Icon'
import messages from './messages'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import useOnClickOutside from 'hooks/useOnClickOutside'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import { isEmail } from 'utils'
import { useBrand } from 'contexts/BrandContext'

const SharingModal = ({ onClose, onSubmitEmail, hasEmail }) => {
  const container = useRef()
  const location = useLocation()
  const uriRef = useRef(null)
  const [requestVotes, setRequestVotes] = useState(null)
  const [email, setEmail] = useState(null)
  const [copied, setCopied] = useState(false)
  const { formatMessage, locale } = useIntl()
  const brand = useBrand()

  useOnClickOutside(container, onClose)
  useKeyboardShortcut(['Escape'], onClose)

  const onCopyToClipBoard = () => {
    uriRef.current.select()
    document.execCommand('copy')
    setCopied(true)
  }

  const pathname = location.pathname;
  const projectId = pathname.substring(pathname.lastIndexOf('/') + 1);
  const votePath = requestVotes ? '/vote' : '';

  const url = `${brand.shareBase}${locale}/voicefinder/project/${projectId}${votePath}`

  const onShareViaLinkedIn = () =>
    window.open(
      `https://www.linkedin.com/shareArticle/?url=${encodeURI(url)}`,
      `_blank`,
      `directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,width=400,height=350`
    )

  const onShareViaEmail = () =>
    window.open(
      `mailto:?subject=${encodeURIComponent(
        formatMessage(messages.emailSubject)
      )}&body=${encodeURIComponent(
        formatMessage(messages.emailBody, { url })
      )}`,
      '_self'
    )

  return (
    <div className="fixed bottom-0 inset-x-0 px-4 pb-4 sm:inset-0 sm:flex sm:items-center sm:justify-center z-50">
      <div className="fixed inset-0 transition-opacity">
        <div className="absolute inset-0 bg-grayscale opacity-50"></div>
      </div>

      <div
        className="bg-white rounded overflow-hidden shadow transform transition-all sm:max-w-lg sm:w-full"
        role="dialog"
        aria-modal="true"
        ref={container}
      >
        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <h3
              className="text-sm leading-6 font-medium text-gray-900 uppercase"
              id="modal-headline"
            >
              <FormattedMessage {...messages.modalTitle} />
            </h3>

            <div className="mt-2 space-y-2 text-sm leading-5 text-gray-900">
              <FormattedMessage {...messages[requestVotes == null ? 'modalBodyVoting' : 'modalBody'] } tagName="p" />

              { (requestVotes && hasEmail) &&
                <FormattedMessage {...messages.requestVotesBody } tagName="p" />
              }
            </div>
          </div>
        </div>

        { requestVotes == null ? (
          <div className="w-full flex justify-center space-x-2 p-4">
            <Button onClick={() => setRequestVotes(true)}>Yes</Button>
            <Button variant="outline" onClick={() => setRequestVotes(false)}>No</Button>
          </div>
        ) : requestVotes == true && !hasEmail ? (
          <form onSubmit={() => onSubmitEmail({email})} className="p-10 space-y-2">
            <Input
              type="email"
              label={formatMessage(messages.emailLabel)}
              description={formatMessage(messages.emailDiscription)}
              placeholder="michael@dunder-mifflin.com"
              value={email}
              onChange={({ target: { value } }) => setEmail(value)}
            />

            <Button
              type="submit"
              disabled={!isEmail(email)}
              onClick={() => onSubmitEmail({email})}
              className="w-full md:w-auto"
            >
              {formatMessage(messages.submitText)}
            </Button>
          </form>
        ) : (
          <>
            <div className="bg-gray-100 px-10 py-4 my-4 text-sm grid grid-cols-6">
              <div className="cursor-pointer col-span-5">
                <input
                  type="uri"
                  value={url}
                  className="w-full h-auto bg-transparent text-gray-700"
                  ref={uriRef}
                  onClick={() => uriRef.current.select()}
                  readOnly
                />
              </div>
              <div className="text-right cursor-pointer hover:text-primary">
                <Icon
                  icon={copied ? 'check' : 'share'}
                  onClick={onCopyToClipBoard}
                />
              </div>
            </div>

            <div className="p-4 sm:px-6 sm:flex sm:flex-row-reverse">
              <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
                <Button variant="outline" className="w-full md:w-auto" onClick={onClose}>
                  <FormattedMessage {...messages.close} />
                </Button>
              </span>
              <span className="mt-3 flex w-full rounded-md shadow-sm sm:ml-3 sm:mt-0 sm:w-auto">
                <Button className="w-full md:w-auto" onClick={onShareViaEmail}>
                  <FormattedMessage {...messages.shareEmail} />
                </Button>
              </span>
              <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
                <Button className="w-full md:w-auto" onClick={onShareViaLinkedIn}>
                  <FormattedMessage {...messages.shareLinkedIn} />
                </Button>
              </span>
            </div>
          </>
        ) }
      </div>
    </div>
  )
}

export default SharingModal
